import { useQuery } from "@tanstack/react-query";
import {
  Archive,
  Check,
  ChevronDown,
  FolderKanban,
  LogOut,
  Map,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Settings,
  ShieldCheck,
  Sparkles,
  UsersRound,
} from "lucide-react";
import { toast } from "sonner";
import { useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { api, clearApiKey } from "../api";
import type { ProjectSummary } from "../types";
import { AnnouncementBanner } from "./announcement-banner";
import { title } from "../format";

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const switcher = useRef<HTMLDetailsElement>(null);
  const utilities = useRef<HTMLDetailsElement>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("migrate-sidebar-collapsed") === "true",
  );
  const projects = useQuery({
    queryKey: ["projects", false],
    queryFn: () => api<{ projects: ProjectSummary[] }>("/api/projects?archived=false"),
  });
  const viewer = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () => api<{ user: { email: string; name: string; role: string } }>("/api/auth/me"),
    staleTime: Infinity,
  });
  const current = projects.data?.projects.find((project) => project.id === projectId);
  const user = viewer.data?.user;
  const userName = user?.name;

  function go(path: string, menu?: React.RefObject<HTMLDetailsElement | null>) {
    menu?.current?.removeAttribute("open");
    navigate(path, { viewTransition: true });
  }

  function signOut() {
    clearApiKey();
    window.location.assign("/");
  }

  function toggleSidebar() {
    setSidebarCollapsed((collapsed) => {
      localStorage.setItem("migrate-sidebar-collapsed", String(!collapsed));
      switcher.current?.removeAttribute("open");
      return !collapsed;
    });
  }

  return (
    <div className={`application-shell${sidebarCollapsed ? " sidebar-collapsed" : ""}`}>
      <aside className="vanta-sidebar" id="project-sidebar">
        <button
          type="button"
          className="sidebar-collapse-button"
          aria-controls="project-sidebar"
          aria-expanded={!sidebarCollapsed}
          aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          onClick={toggleSidebar}
        >
          {sidebarCollapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
        </button>
        <button className="wordmark" onClick={() => navigate("/", { viewTransition: true })} aria-label="Projects home">
          <span className="brand-mark brand-mark-small">P</span>
          <span>ProSights <b>Migrate</b></span>
        </button>

        <details className="project-switcher" ref={switcher}>
          <summary aria-label="Switch migration" title="Switch migration">
            <span>
              <strong>{current?.name || (location.pathname === "/projects/new" ? "New migration" : "All migrations")}</strong>
              <small>{current ? `${title(current.source_connector)} → ${title(current.destination_connector)}` : "Shared workspace"}</small>
            </span>
            <ChevronDown size={14} />
          </summary>
          <div className="project-switcher-menu">
            <header>
              <span>Migrations</span>
              <small>{projects.data?.projects.length || 0}</small>
            </header>
            <div className="project-switcher-list">
              {projects.data?.projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => go(`/projects/${project.id}/overview`, switcher)}
                >
                  <span className="project-switcher-icon">{project.name.slice(0, 1).toUpperCase()}</span>
                  <span>
                    <strong>{project.name}</strong>
                    <small>{title(project.source_connector)} → {title(project.destination_connector)}</small>
                  </span>
                  {project.id === projectId ? <Check size={14} /> : <small>{project.readiness}%</small>}
                </button>
              ))}
            </div>
            <footer>
              <button onClick={() => go("/", switcher)}><FolderKanban size={14} /> All migrations</button>
              <button onClick={() => go("/projects/new", switcher)}><Plus size={14} /> New migration</button>
            </footer>
          </div>
        </details>

        <nav className="vanta-nav" aria-label="Project utilities">
          <small>Project</small>
          <NavLink aria-label={current ? "Roadmap" : "Migrations"} title={current ? "Roadmap" : "Migrations"} to={current ? `/projects/${current.id}/overview` : "/"} end viewTransition>
            <Map size={16} /><span>{current ? "Roadmap" : "Migrations"}</span>
          </NavLink>
          {current ? (
            <NavLink aria-label="Organization" title="Organization" to={`/projects/${current.id}/organization`} viewTransition>
              <UsersRound size={16} /><span>Organization</span>
            </NavLink>
          ) : null}
          <small>Workspace</small>
          <NavLink aria-label="Archive" title="Archive" to="/archive" viewTransition><Archive size={16} /><span>Archive</span></NavLink>
        </nav>

        <button className="vanta-new-migration" aria-label="New migration" title="New migration" onClick={() => navigate("/projects/new", { viewTransition: true })}>
          <Plus size={15} /><span>New migration</span>
        </button>
      </aside>

      <div className="vanta-main">
        <AnnouncementBanner />
        <header className="topbar">
          {current ? (
            <div className="agent-presence">
              <span><Sparkles size={13} /></span>
              <div><small>Migration agent</small><strong>{current.open_decisions ? `${current.open_decisions} decisions need review` : "Ready for the next object"}</strong></div>
            </div>
          ) : <span />}
          <div className="topbar-actions">
            <span className="simulation-badge">
              <ShieldCheck size={13} />
              Zero-write mode
            </span>
            <button
              type="button"
              className="topbar-icon-button"
              aria-label="Settings"
              title="Settings"
              onClick={() => toast.info("Settings aren’t available yet", {
                description: "This area will be enabled in a later build.",
              })}
            >
              <Settings size={16} />
            </button>
            <details className="utility-menu" ref={utilities}>
              <summary
                className="user-avatar"
                aria-label={userName ? `Account menu for ${userName}` : "Account menu"}
                title={userName ? `${userName}${user?.email ? ` · ${user.email}` : ""}` : "Account menu"}
              >
                {initials(userName)}
              </summary>
              <div>
                <small>Workspace</small>
                <button onClick={() => go("/archive", utilities)}><Archive size={14} /> Archived migrations</button>
                <button onClick={signOut}><LogOut size={14} /> Sign out</button>
              </div>
            </details>
          </div>
        </header>
        <div className="content-frame">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

function initials(name = "ProSights") {
  return name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
}
